/* import __COLOCATED_TEMPLATE__ from './nav-native-content-link.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import Folder from 'embercom/models/content-service/folder';
import { type TreeItem } from 'embercom/objects/tree-list';
import type IntlService from 'embercom/services/intl';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type FolderMembership from 'embercom/models/content-service/folder-membership';
import type RouterService from '@ember/routing/router-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { action } from '@ember/object';
import { type NavigationLinkData } from './types';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import { KNOWLEDGE_BLANK_QUERY_PARAMS } from 'embercom/lib/knowledge-hub/list-api';
import { tracked } from 'tracked-built-ins';

interface Args {
  item: TreeItem<Folder | FolderMembership | NavigationLinkData | undefined>;
}

export default class NavNativeContentLink extends Component<Args> {
  @service intl!: IntlService;
  @service notificationsService: any;
  @service appService: any;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intercomEventService: any;
  @service declare router: RouterService;
  @service declare permissionsService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;
  @tracked modalOpen = false;

  get folder(): Folder | undefined {
    if (this.args.item.dataObject instanceof Folder) {
      return this.args.item.dataObject;
    }
    return undefined;
  }

  get navigationLinkItem(): TreeItem<NavigationLinkData> | undefined {
    // This component specifically handles NavigationLinkData items
    // Compare with the canonical native content tree item from the service
    if (this.args.item === this.knowledgeHubService.nativeContentTreeItem) {
      return this.args.item as TreeItem<NavigationLinkData>;
    }
    return undefined;
  }

  get itemName(): string | undefined {
    let dataObject = this.args.item.dataObject;
    if (dataObject && 'name' in dataObject) {
      return (dataObject as NavigationLinkData).name;
    }
    return undefined;
  }

  get isActive() {
    return this.isOnAllContentRoute && this.hasNativeContentParam;
  }

  get isOnAllContentRoute(): boolean {
    return this.router.currentRouteName === 'apps.app.knowledge-hub.all-content';
  }

  get hasNativeContentParam(): boolean {
    let param = this.router.currentRoute?.queryParams?.native_content;
    return param === 'true';
  }

  @action
  toggleIsExpanded(event: MouseEvent) {
    event.preventDefault();
    this.router.transitionTo('apps.app.knowledge-hub.all-content', {
      queryParams: { ...KNOWLEDGE_BLANK_QUERY_PARAMS, native_content: true },
    });

    this.args.item.isExpanded = !this.args.item.isExpanded;

    this.intercomEventService.trackAnalyticsEvent({
      action: this.args.item.isExpanded ? 'expanded' : 'collapsed',
      object: 'navigation_link_item',
      place: 'knowledge-hub',
      section: 'nav-list-navigation-link',
    });
  }

  @action
  async openModal() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }

    this.modalOpen = true;
  }

  @action
  closeModal() {
    this.modalOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Folders::Tree::NavNativeContentLink': typeof NavNativeContentLink;
    'knowledge-hub/folders/tree/nav-native-content-link': typeof NavNativeContentLink;
  }
}
